import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";

import {
  formatSealConfirmCopy,
  formatSealDestinationLine,
  sealConfirmPrimaryLabel,
  type SealCartonPreviewResponse,
} from "./prepackSealCarton";

export function PrePackSealCartonModal({
  open,
  preview,
  previewLoading,
  previewError,
  confirming,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  preview: SealCartonPreviewResponse | null;
  previewLoading: boolean;
  previewError: string | null;
  confirming: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape" && !confirming) onCancel();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, confirming, onCancel]);

  if (!open) return null;

  const sealedNumber = preview?.sealedCartonNumber ?? null;
  const primary = preview?.primaryDestinationCartonNumber ?? null;

  return createPortal(
    <div {...hubModalOverlayProps(true)}>
      <Card className="mt-6 w-full max-w-lg shadow-xl" role="dialog" aria-modal="true">
        <CardHeader className="border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Seal carton</h2>
          <p className="mt-1 text-sm text-slate-600">
            Mark this physical carton sealed and move remaining work to the destination cartons below.
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          {previewLoading ? (
            <p className="flex items-center gap-2 text-sm text-slate-600">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading remainder placement…
            </p>
          ) : previewError ? (
            <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              {previewError}
            </p>
          ) : preview && sealedNumber != null ? (
            <div className="space-y-3 text-sm text-slate-700">
              <ul className="list-disc space-y-1 pl-5">
                {preview.destinationCartons.map((destination) => (
                  <li key={destination.cartonNumber}>{formatSealDestinationLine(destination)}</li>
                ))}
              </ul>
              <p className="whitespace-pre-line rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
                {formatSealConfirmCopy(sealedNumber, preview.destinationCartons)}
              </p>
            </div>
          ) : (
            <p className="text-sm text-slate-600">No preview available.</p>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" disabled={confirming} onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={confirming || previewLoading || previewError != null || preview == null || primary == null}
              onClick={onConfirm}
            >
              {confirming ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
              {primary != null ? sealConfirmPrimaryLabel(primary) : "Seal carton"}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}
